import { useState } from "react";
import {
  Box,
  IconButton,
  TextareaAutosize,
  Tooltip,
  Typography,
} from "@mui/material";
import { X, Check, Link } from "lucide-mui";
import { styledComponent } from "../utils/styled";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { getValidChunkIds } from "../utils/chunkUtils";
import { FieldNotesModal } from "./FieldNotesModal";
import { SourceFilesPanel } from "./SourceFilesPanelComponent";
import { CircleCheckIcon } from "./icons";

// ── Styled components ─────────────────────────────────────────────────────────

const EditorContainer = styledComponent(Box)(({ isDark }) => ({
  backgroundColor: isDark ? "#1F2123" : "#F5F6F8",
  borderRadius: "20px",
  paddingTop: "20px",
  paddingBottom: "20px",
  paddingLeft: "20px",
  paddingRight: "20px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  margin: "16px",
  marginTop: "0",
  height: "140px",
  boxSizing: "border-box",
}));

const TopRow = styledComponent(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
});

const StepBadge = styledComponent(Box)(({ isDark }) => ({
  minWidth: "22px",
  height: "22px",
  borderRadius: "6px",
  backgroundColor: isDark ? "#3f4043" : "#FFFFFF",
  border: isDark ? "none" : "1px solid #d5d9df",
  color: isDark ? "#f4f4fb" : "#31363b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: isDark ? 500 : 400,
  flexShrink: 0,
  marginTop: "6px",
}));

const DescriptionWrapper = styledComponent(Box)({
  flex: 1,
  minWidth: 0,
  position: "relative",
});

const SaveCancelRow = styledComponent(Box)({
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: "2px",
  display: "flex",
  gap: "4px",
  zIndex: 10,
});

const CancelEditBtn = styledComponent(Box)(({ isDark }) => ({
  width: "30px",
  height: "30px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backgroundImage: isDark
    ? "linear-gradient(135deg, #6E7379 0%, #53585D 50%, #4D5156 100%)"
    : "linear-gradient(135deg, #CFD3D8 0%, #C8CCD1 50%, #BDC1C6 100%)",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1), 0px 10px 15px rgba(0,0,0,0.1)",
}));

const SaveEditBtn = styledComponent(Box)({
  width: "30px",
  height: "30px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backgroundImage: "linear-gradient(90deg, #146E37 0%, #1a8744 100%)",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1), 0px 10px 15px rgba(0,0,0,0.1)",
});

const SourceLinkBtn = styledComponent(IconButton)(({ isDark }) => ({
  backgroundImage: isDark
    ? "linear-gradient(135deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
    : "linear-gradient(135deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
  border: "1px solid",
  borderColor: isDark ? "rgba(106,112,121,0.5)" : "#d5d9df",
  color: isDark ? "#b3b3b3" : "#6B7280",
  borderRadius: "8px",
  width: "30px",
  height: "30px",
  flexShrink: 0,
  transition: "opacity 0.2s",
  marginTop: "6px",
}));

const BottomRow = styledComponent(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "20px",
  alignItems: "center",
});

const ActionBtn = styledComponent(Box)(({ isDark }) => ({
  height: "36px",
  width: "97px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  backgroundImage: isDark
    ? "linear-gradient(159.6deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
    : "linear-gradient(159.4deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
  border: "1px solid",
  borderColor: isDark ? "rgba(106,112,121,0.5)" : "#d5d9df",
}));

const ActionBtnText = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.021px",
  color: isDark ? "#eceff3" : "#434950",
  userSelect: "none",
}));

const CompleteStatusText = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  fontStyle: "italic",
  color: isDark ? "#41D179" : "#146E37",
  userSelect: "none",
}));

// ── DescriptionField sub-component ────────────────────────────────────────────

const EditingBox = styledComponent(Box)(({ isDark }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "flex-start",
  backgroundColor: isDark ? "#282b2e" : "#FFFFFF",
  border: "1px solid",
  borderColor: isDark ? "#424549" : "#d5d9df",
  borderRadius: "10px",
  padding: "6px 12px",
  overflow: "hidden",
}));

const DisplayBox = styledComponent(Box)(({ isDark, isHovered, readOnly }) => ({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
  padding: "6px 12px",
  border: "1px solid transparent",
  borderRadius: "10px",
  cursor: readOnly ? "default" : "text",
  backgroundColor:
    !readOnly && isHovered
      ? isDark
        ? "rgba(58,58,59,0.5)"
        : "#e4eaf1"
      : "transparent",
  transition: "background-color 0.15s",
}));

const DisplayText = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "20px",
  color: isDark ? "#f1f4f8" : "#31363b",
  margin: 0,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
}));

function DescriptionField({
  value,
  onChange,
  placeholder,
  isDark,
  onEditingChange,
  readOnly,
  imageMeta,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const setEditing = val => {
    if (readOnly) return;
    setIsEditing(val);
    onEditingChange?.(val);
  };

  return (
    <>
      {isEditing ? (
        <EditingBox isDark={isDark}>
          <TextareaAutosize
            autoFocus
            value={value ?? ""}
            minRows={1}
            maxRows={2}
            onChange={e => onChange(e.target.value)}
            onBlur={() => setEditing(false)}
            placeholder={placeholder}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "20px",
              color: isDark ? "#f1f4f8" : "#31363b",
              padding: 0,
              resize: "none",
              display: "block",
            }}
          />
        </EditingBox>
      ) : (
        <DisplayBox
          onClick={readOnly ? undefined : () => setEditing(true)}
          onMouseEnter={readOnly ? undefined : () => setIsHovered(true)}
          onMouseLeave={readOnly ? undefined : () => setIsHovered(false)}
          isDark={isDark}
          isHovered={isHovered}
          readOnly={readOnly}
        >
          <DisplayText isDark={isDark}>
            {value || (
              <span style={{ opacity: 0.35, fontStyle: "italic" }}>
                {placeholder}
              </span>
            )}
          </DisplayText>
        </DisplayBox>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const StepContentEditorComponent = ({
  stepNumber,
  stepData,
  currentStepData,
  setCurrentStepData,
  onSave,
  onCancel,
  onSaveNote,
  onComplete,
  isLogbookActive = false,
  isStepCompleted = false,
  isPrevStepCompleted = true,
  logbookFieldNote = null,
  readOnly = false,
  sessionId = null,
  fileId = null,
  anchorId = null,
  dpmUuid = null,
  onDpmUpdate = null,
  onEditBlocked = null,
}) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [isDescEditing, setIsDescEditing] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [sourcePanelAnchor, setSourcePanelAnchor] = useState(null);

  const validChunkIds = getValidChunkIds(stepData ?? {});
  const hasFiles = validChunkIds.length > 0;
  const showSourceButton = !readOnly || hasFiles;
  // Backend (dpm.py) already resolves the step's reference_id/reference_type to
  // a filename and filters image_metadata down to the matching image(s) — the
  // step data itself has no plain filename field to match against here.
  const imageMeta = stepData?.image_metadata?.[0] ?? null;
  console.log(
    "[IMG_PIPELINE:frontend] stepData.image_metadata=",
    stepData?.image_metadata,
    "computed imageMeta=",
    imageMeta
  );

  return (
    <EditorContainer isDark={isDark}>
      <TopRow>
        <StepBadge isDark={isDark}>{stepNumber}</StepBadge>

        <DescriptionWrapper>
          <DescriptionField
            value={currentStepData?.description}
            onChange={val =>
              setCurrentStepData?.(prev => ({ ...prev, description: val }))
            }
            placeholder='Enter step description here...'
            isDark={isDark}
            onEditingChange={setIsDescEditing}
            readOnly={!setCurrentStepData}
            imageMeta={imageMeta}
          />
          {isDescEditing && (
            <SaveCancelRow>
              <CancelEditBtn
                isDark={isDark}
                onMouseDown={e => e.preventDefault()}
                onClick={() => onCancel?.("update_step")}
              >
                <X
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: isDark ? "#ffffff" : "#31363B",
                  }}
                />
              </CancelEditBtn>
              <SaveEditBtn
                onMouseDown={e => e.preventDefault()}
                onClick={() => onSave?.("update_step")}
              >
                <Check
                  sx={{ width: "16px", height: "16px", color: "#FFFFFF" }}
                />
              </SaveEditBtn>
            </SaveCancelRow>
          )}
        </DescriptionWrapper>

        {showSourceButton && (
          <Tooltip title='Source files' arrow placement='top'>
            <span>
              <SourceLinkBtn
                isDark={isDark}
                onClick={e => setSourcePanelAnchor(e.currentTarget)}
                size='small'
              >
                <Link sx={{ width: "14px", height: "14px" }} />
              </SourceLinkBtn>
            </span>
          </Tooltip>
        )}
        <SourceFilesPanel
          anchorEl={sourcePanelAnchor}
          open={Boolean(sourcePanelAnchor)}
          onClose={() => setSourcePanelAnchor(null)}
          stepData={stepData}
          stepNumber={stepNumber}
          sessionId={sessionId}
          fileId={fileId}
          userId={user?.id}
          anchorId={anchorId}
          dpmUuid={dpmUuid}
          readOnly={readOnly}
          onDpmUpdate={onDpmUpdate}
          onEditBlocked={onEditBlocked}
        />
      </TopRow>

      {isLogbookActive && (
        <BottomRow>
          <ActionBtn isDark={isDark} onClick={() => setNoteModalOpen(true)}>
            <ActionBtnText isDark={isDark}>
              {logbookFieldNote ? "Edit Note" : "Add Note"}
            </ActionBtnText>
          </ActionBtn>
          {isStepCompleted ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <CircleCheckIcon
                circleColor={isDark ? "#41D179" : "#146E37"}
                checkColor={isDark ? "#262A2E" : "#F1F4F8"}
                sx={{
                  width: "16px",
                  height: "16px",
                }}
              />
              <CompleteStatusText isDark={isDark}>Complete</CompleteStatusText>
            </Box>
          ) : isPrevStepCompleted ? (
            <ActionBtn isDark={isDark} onClick={onComplete}>
              <ActionBtnText isDark={isDark}>Complete</ActionBtnText>
            </ActionBtn>
          ) : null}
        </BottomRow>
      )}

      <FieldNotesModal
        open={noteModalOpen}
        stepNumber={stepNumber}
        stepName={currentStepData?.name ?? ""}
        initialNote={
          isLogbookActive
            ? (logbookFieldNote ?? "")
            : (currentStepData?.note ?? "")
        }
        onAdd={note => onSaveNote?.(note)}
        onClose={() => setNoteModalOpen(false)}
      />
    </EditorContainer>
  );
};
